import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FreeMode, Mousewheel } from 'swiper/modules';
import type { Swiper } from 'swiper/types';
import { useSportUrlParams } from '@mwl/core-lib';
import ArrowRightIcon from '@public/assets/common/icons/arrowRightOutlined.svg';
import type { ButtonAsButton } from '@/components/Button/Button';
import { Button } from '@/components/Button/Button';
import { ButtonIcon } from '@/components/ButtonIcon/ButtonIcon';
import type { SliderProps } from '@/components/Slider/Slider';
import { Slider } from '@/components/Slider/Slider';
import { SportIcon } from '@/components/SportIcon/SportIcon';
import type { SportSearchEventsProps } from './SportSearchEvents.types';
import styles from './SportSearchEvents.module.scss';
const commonButtonProps: ButtonAsButton = {
  as: 'button',
  rounded: true,
  size: 'sm',
  variant: 'filled',
  color: 'black-light',
  cut: false
};
const sliderProps: SliderProps = {
  classes: {
    wrapper: styles.slider,
    endShadow: styles.sliderEndShadow,
    startShadow: styles.sliderStartShadow
  },
  slidesPerView: 'auto',
  spaceBetween: 0,
  breakpoints: {
    768: {
      spaceBetween: 8
    }
  },
  freeMode: true,
  isEndShadow: true,
  isStartShadow: true,
  modules: [Mousewheel, FreeMode],
  mousewheel: {
    forceToAxis: true
  }
};
const SportSearchEvents: FC<SportSearchEventsProps> = ({
  className,
  events
}) => {
  const prevRef = useRef<HTMLDivElement>(null);
  const nextRef = useRef<HTMLDivElement>(null);
  const [swiper, setSwiper] = useState<Swiper>();
  const {
    setFilters
  } = useSportUrlParams();
  const handleClick = useCallback((eventId: number) => {
    setFilters({
      searchSportId: eventId.toString()
    });
  }, [setFilters]);
  const activeEvent = useMemo(() => events?.find(event => event.selected), [events]);
  const otherEvents = useMemo(() => events?.filter(event => event.id !== activeEvent?.id), [events, activeEvent]);
  useEffect(() => {
    if (!swiper) {
      return;
    }
    const {
      navigation
    } = swiper.params;
    if (typeof navigation === 'boolean' || navigation == null) {
      return;
    }
    navigation.prevEl = prevRef.current;
    navigation.nextEl = nextRef.current;
    navigation.disabledClass = styles.disabledNav;
    swiper.navigation.init();
  }, [swiper]);
  useEffect(() => {
    swiper?.navigation?.update();
  }, [swiper, otherEvents]);
  if (!otherEvents || !otherEvents.length) {
    return null;
  }
  return <div className={cn(styles.root, className)} data-sentry-component="SportSearchEvents" data-sentry-source-file="SportSearchEvents.tsx">
      <div className={styles.sliderWrapper}>
        <div ref={prevRef} className={styles.navLeft}>
          <ButtonIcon as="button" variant="text" size="auto" aria-label="search events prev" data-sentry-element="ButtonIcon" data-sentry-source-file="SportSearchEvents.tsx">
            <ArrowRightIcon width={20} height={20} data-sentry-element="ArrowRightIcon" data-sentry-source-file="SportSearchEvents.tsx" />
          </ButtonIcon>
        </div>
        <Slider {...sliderProps} onSwiper={setSwiper} data-sentry-element="Slider" data-sentry-source-file="SportSearchEvents.tsx">
          {[activeEvent, ...otherEvents].map(event => {
          return <Button key={event?.id} className={cn(styles.button, {
            [styles.active]: event?.selected
          })} title={event?.title} onClick={() => handleClick(event?.id ?? 0)} {...commonButtonProps}>
                <SportIcon url={event?.icon} className={styles.icon} size={24} />
                <span className={styles.title}>{event?.title}</span>
                {!!event?.count && <span className={styles.count}>{event?.count}</span>}
              </Button>;
        })}
        </Slider>
        <div ref={nextRef} className={styles.navRight}>
          <ButtonIcon as="button" variant="text" size="auto" aria-label="search events next" data-sentry-element="ButtonIcon" data-sentry-source-file="SportSearchEvents.tsx">
            <ArrowRightIcon width={20} height={20} data-sentry-element="ArrowRightIcon" data-sentry-source-file="SportSearchEvents.tsx" />
          </ButtonIcon>
        </div>
      </div>
    </div>;
};
export { SportSearchEvents };